/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What a comparison answers when a side does not resolve** (`#1384`, ruled by Todd 2026-09-19).
 *
 * One rule with a writer and a reader on opposite sides of the mirror: the engine **answers** by it
 * (`macro-expression.engine.ts`), and the console's structural findings **explain** it — *"has no
 * value to compare with, so it never holds"*. Written as a condition in each, the two would be free to
 * disagree, which is `#1269`; named once, they cannot.
 *
 * | | Answer | Why |
 * | --- | --- | --- |
 * | `exists` / `notExists` | *(not this rule)* | they **are** the question *does it resolve*, answered by resolution itself |
 * | `neq` | **true** | it is exactly `not eq` — *"genre neq kids"* holds when genre is absent |
 * | every other operator | **false** | fail closed: no answer is no value (`#1300`), in the direction of `#1195` |
 *
 * "Does not resolve" includes a *Compare with* that was never set — the engine cannot tell the two
 * apart — and **both sides absent**: two missing values are not equal. The cost is ruled rather than
 * accidental: *"If the true test is for absent the user can start with 'exists…'."*
 *
 * **It imports nothing**, which is why the operator is a `string` here rather than the vocabulary
 * `macro-expression.dto.ts` declares: the console crossing then brings no second file with it. Both
 * callers pass a `ConditionOperator`.
 *
 * @returns the answer, or `null` for the presence pair, which this rule does not decide.
 */
export function comparisonWhenUnresolved(operator: string): boolean | null {
  if (operator === 'exists' || operator === 'notExists') return null;
  return operator === 'neq';
}
